import {
    centsToDollars,
    formatDollars,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import { Redirect, router, useFocusEffect } from 'expo-router';
import { useAtom } from 'jotai';
import { useRef, useState } from 'react';
import type { TextInput } from 'react-native';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    DollarInput,
    ScreenContainer,
    Switch,
    XStack,
    YStack,
} from 'smoothie';

import { apexWithdrawalRequestAtom } from '../../state/investmentAtoms';

export const WithdrawalAmount = () => {
    const inputRef = useRef<TextInput>(null);
    useFocusEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    });

    const [apexWithdrawalRequest, setApexWithdrawalRequest] = useAtom(
        apexWithdrawalRequestAtom,
    );

    const [amount, setAmount] = useState<string | undefined>();
    const [isLiquidated, setIsLiquidated] = useState<boolean>(false);

    const { fromAccount, isRetirementAccount } = apexWithdrawalRequest ?? {};
    const availableLiquidity = centsToDollars(
        fromAccount?.realtimeBalanceData.accountEquityCents ?? 0,
    );

    if (apexWithdrawalRequest?.toAccount == null) {
        return <Redirect href="/home/investment" />;
    }

    if (apexWithdrawalRequest?.fromAccount == null) {
        return <Redirect href="/home/investment" />;
    }

    const amountToTransferCents = formattedDollarsToCents(amount ?? '0.00');

    const isValid = amountToTransferCents > 0;

    return (
        <ScreenContainer hasBackButton={true} title="Withdraw funds">
            <YStack gap={24} padding={2}>
                <DollarInput
                    ref={inputRef}
                    marginVertical={64}
                    allowDecimals={true}
                    value={amount}
                    onChangeText={(amt) => {
                        setAmount(amt);
                        if (
                            isLiquidated &&
                            amt !== availableLiquidity.toString()
                        ) {
                            setIsLiquidated(false);
                        }
                    }}
                    placeholder="$0.00"
                />
                <YStack
                    gap={16}
                    borderRadius={24}
                    backgroundColor="$gray200"
                    paddingVertical={16}
                    paddingHorizontal={24}
                    justifyContent="center"
                >
                    <XStack
                        justifyContent="space-between"
                        gap={16}
                        alignContent="center"
                        alignItems="center"
                    >
                        <YStack alignItems="flex-start">
                            <BodyMediumRegular>
                                Sell entire portfolio
                            </BodyMediumRegular>
                            <BodySmallRegular color="$gray1000">
                                {formatDollars(availableLiquidity)} current
                                balance
                            </BodySmallRegular>
                        </YStack>
                        <Switch
                            isChecked={isLiquidated}
                            onCheckedChange={(checked) => {
                                setIsLiquidated(checked);
                                setAmount(() => {
                                    return availableLiquidity.toString();
                                });
                            }}
                        />
                    </XStack>
                </YStack>
                <Button
                    disabled={!isValid}
                    text="Continue"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={() => {
                        setApexWithdrawalRequest({
                            ...apexWithdrawalRequest,
                            amountCents: amountToTransferCents,
                            liquidateAccount: isLiquidated,
                        });
                        isRetirementAccount
                            ? router.navigate(
                                  '/investments/transfer/withdrawal-reason',
                              )
                            : router.navigate(
                                  '/investments/transfer/withdrawal-review',
                              );
                    }}
                />
            </YStack>
        </ScreenContainer>
    );
};
